import { ChevronRight, PhoneMissed } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { formatPhone } from "../../../../lib/phone";
import { fmtDuration, fmtStamp } from "../../../ui";
import { useWorkCompact } from "../../_shared/density";
import { summarizeThread, type CallThread } from "./group";

/**
 * The folded line that stands for every call with one number.
 *
 * It is built out of the same vocabulary as `CallItem` on purpose - the same
 * tile, the same two text lines, the same right-hand column - because it sits
 * in the same column as ordinary rows and a second row shape would read as a
 * second kind of thing. What differs is what each part says.
 *
 * THE TILE CARRIES THE COUNT, not a direction. A thread usually holds both
 * directions and several outcomes, so any one icon would be a claim about the
 * whole group that is only true of one call in it. The count is the fact that
 * is true of all of them, and it is the reason the line is folded at all.
 *
 * THE MISSED TAG IS THE ONE ALARM LEFT ON. Folding hides the red tiles of the
 * rows underneath, so an operator scanning a threaded log would lose exactly
 * the rows they scan for. The tag puts that number back on the line. It counts
 * only rung-out and busy calls - see `summarizeThread`, which deliberately
 * leaves out a caller the blocklist refused.
 *
 * The right column shows the talk time of the WHOLE thread against the stamp
 * of its newest call. Both are about the group, so neither invites the reading
 * that this line is really one of the calls inside it.
 */
export function CallThreadRow({
  thread,
  open,
  onToggle,
  /** True while one of the calls inside is the one the detail pane holds. */
  active,
}: {
  thread: CallThread;
  open: boolean;
  onToggle: () => void;
  active: boolean;
}) {
  const t = useT();
  const compact = useWorkCompact();
  const { missed, seconds, companyName } = summarizeThread(thread);

  const shown = thread.number ? formatPhone(thread.number) : t("calls.incoming.withheld");
  const count = t("calls.thread.count", { count: String(thread.rows.length) });
  // The number is only worth a second line when the first one is a name. With
  // no company we would be printing the same digits twice.
  const meta = companyName ? `${shown} · ${count}` : count;

  return (
    <button
      type="button"
      className={cn("crm-item crm-group-h items-center", active && !open && "is-on")}
      aria-expanded={open}
      onClick={onToggle}
      title={open ? t("calls.thread.collapse") : t("calls.thread.expand")}
    >
      <ChevronRight className={cn("crm-group-chev size-4", open && "is-open")} strokeWidth={2.5} />

      <span className="crm-cdir is-thread" aria-hidden="true">
        {thread.rows.length}
      </span>

      <span className="min-w-0 flex-1 text-left">
        <span className="crm-item-from block truncate">{companyName ?? shown}</span>
        <span className={cn("flex items-center gap-1.5", compact ? "mt-[3px]" : "mt-[5px]")}>
          <span className="crm-item-pre min-w-0 flex-1 truncate">{meta}</span>
          {missed ? (
            <span className="crm-ctag is-miss" title={t("calls.thread.missedHint")}>
              <PhoneMissed className="size-2.5" />
              {missed}
            </span>
          ) : null}
        </span>
      </span>

      <span className="flex-none text-right">
        <span className={cn("block font-bold tabular-nums", compact ? "text-[11.75px]" : "text-[12.25px]")}>
          {seconds ? fmtDuration(seconds) : "-"}
        </span>
        <span className={cn("mt-[3px] block text-muted-foreground", compact ? "text-[10.5px]" : "text-[11px]")}>
          {fmtStamp(thread.rows[0]!.startedAt)}
        </span>
      </span>
    </button>
  );
}
